import { parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";
import { Suspense, useDeferredValue, useState } from "react";
import {
  DateField,
  DateInput,
  DateSegment,
  type DateValue,
  Heading,
} from "react-aria-components";

import { Button } from "@/components/base/button";
import {
  Disclosure,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { useAppForm } from "@/components/base/form";
import {
  FilterDropdown,
  segmentStyles,
} from "@/components/base/form/fields/division-picker";
import { inputStyles } from "@/components/base/form/fields/shared";
import { Link } from "@/components/base/link";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@/components/base/table";
import { TextField } from "@/components/base/text-field";
import { divisionsQueryOptions } from "@/data/divisions";
import type { Division } from "@/db/schema";
import { bulkUpsertInviteSourcesMutationOptions } from "@/functions/invites/bulk-upsert-invite-sources";
import {
  type getInviteSourcesFn,
  getInviteSourcesQueryOptions,
} from "@/functions/invites/get-invite-sources";
import {
  type getInvitesFn,
  getInvitesQueryOptions,
} from "@/functions/invites/get-invites";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";

export type ManageInvitesFormProps = {
  tournamentId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ManageInvitesForm({
  tournamentId,
  onOpenChange,
  ...props
}: ManageInvitesFormProps) {
  return (
    <Modal {...props} onOpenChange={onOpenChange} size="2xl">
      <div className="flex flex-col space-y-6 p-4">
        <Heading className={title({ size: "sm" })} slot="title">
          Manage Invite Sources
        </Heading>

        <Suspense fallback={<div>Loading...</div>}>
          <ManageInvitesContent tournamentId={tournamentId} />
        </Suspense>
      </div>
    </Modal>
  );
}

function ManageInvitesContent({ tournamentId }: { tournamentId: number }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: sources } = useSuspenseQuery(
    getInviteSourcesQueryOptions(tournamentId),
  );

  const { data: invites } = useSuspenseQuery(
    getInvitesQueryOptions(tournamentId),
  );

  const invalidateSources = () => {
    queryClient.invalidateQueries(getInviteSourcesQueryOptions(tournamentId));
  };

  const invalidateInvites = () => {
    queryClient.invalidateQueries(getInvitesQueryOptions(tournamentId));
  };

  const { mutate: removeSource } = useMutation({
    ...trpc.invites.removeInviteSource.mutationOptions(),
    onSuccess: invalidateSources,
  });

  const { mutate: revokeInvite } = useMutation({
    ...trpc.invites.revokeInvite.mutationOptions(),
    onSuccess: invalidateInvites,
  });

  return (
    <div className="flex flex-col gap-6">
      <InviteSourcesSection
        tournamentId={tournamentId}
        sources={sources}
        onRemove={(id) => removeSource({ id })}
        onSuccess={invalidateSources}
      />

      <CurrentInvitesSection
        invites={invites}
        onRevoke={(id) => revokeInvite({ id })}
      />
    </div>
  );
}

type InviteSource = Awaited<ReturnType<typeof getInviteSourcesFn>>[number];

function InviteSourcesSection({
  tournamentId,
  sources,
  onRemove,
  onSuccess,
}: {
  tournamentId: number;
  sources: InviteSource[];
  onRemove: (id: number) => void;
  onSuccess: () => void;
}) {
  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });

  return (
    <div>
      <BulkAddInviteSourcesForm
        tournamentId={tournamentId}
        existingSourceIds={sources.map((s) => s.sourceTournamentDivision.id)}
        onSuccess={onSuccess}
      />

      {sources.length > 0 ? (
        <Disclosure card={false}>
          <DisclosureHeader card={false} size="sm">
            Existing Sources ({sources.length})
          </DisclosureHeader>
          <DisclosurePanel card={false}>
            <Table aria-label="Invite sources">
              <TableHeader>
                <TableColumn isRowHeader>Source Division</TableColumn>
                <TableColumn width={64}>Top N</TableColumn>
                <TableColumn width={48}> </TableColumn>
              </TableHeader>
              <TableBody>
                {sources.map((source) => {
                  const src = source.sourceTournamentDivision;
                  const divisionLabel = getTournamentDivisionDisplay(src);
                  const date = dateFormatter.format(
                    parseDate(src.tournament.date).toDate(getDefaultTimeZone()),
                  );
                  const label = `${divisionLabel} - ${src.tournament.venue.name}, ${src.tournament.venue.city} (${date})`;
                  return (
                    <TableRow key={source.id}>
                      <TableCell>
                        <Link
                          to="/tournaments/$tournamentId/$divisionId"
                          params={{
                            tournamentId: String(src.tournament.id),
                            divisionId: String(src.id),
                          }}
                          target="_blank"
                        >
                          {label}
                        </Link>
                      </TableCell>
                      <TableCell>{source.topN}</TableCell>
                      <TableCell>
                        <Button
                          variant="icon"
                          size="sm"
                          onPress={() => onRemove(source.id)}
                        >
                          <Trash2Icon size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DisclosurePanel>
        </Disclosure>
      ) : (
        <p className="text-sm text-gray-500">No invite sources configured.</p>
      )}
    </div>
  );
}

type Invite = Awaited<ReturnType<typeof getInvitesFn>>[number];

function CurrentInvitesSection({
  invites,
  onRevoke,
}: {
  invites: Invite[];
  onRevoke: (id: number) => void;
}) {
  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });

  if (invites.length === 0) {
    return <p className="text-sm text-gray-500">No invites yet.</p>;
  }

  return (
    <Disclosure card={false}>
      <DisclosureHeader card={false} size="sm">
        Invites ({invites.length})
      </DisclosureHeader>
      <DisclosurePanel card={false}>
        <Table aria-label="Current invites">
          <TableHeader>
            <TableColumn isRowHeader>Player</TableColumn>
            <TableColumn>Source</TableColumn>
            <TableColumn width={48}> </TableColumn>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => {
              const p = invite.playerProfile;
              const playerName = `${p.preferredName ?? p.firstName} ${p.lastName}`;
              const src = invite.source?.sourceTournamentDivision;
              const sourceLabel = src
                ? `${getTournamentDivisionDisplay(src)} (${dateFormatter.format(parseDate(src.tournament.date).toDate(getDefaultTimeZone()))})`
                : "Manual";

              return (
                <TableRow key={invite.id}>
                  <TableCell>{playerName}</TableCell>
                  <TableCell>{sourceLabel}</TableCell>
                  <TableCell>
                    <Button
                      variant="icon"
                      size="sm"
                      onPress={() => onRevoke(invite.id)}
                    >
                      <Trash2Icon size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DisclosurePanel>
    </Disclosure>
  );
}

const BULK_PAGE_SIZE = 200;

function BulkAddInviteSourcesForm({
  tournamentId,
  existingSourceIds,
  onSuccess,
}: {
  tournamentId: number;
  existingSourceIds: number[];
  onSuccess: () => void;
}) {
  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });
  const [filterDivisionIds, setFilterDivisionIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [filterVenueIds, setFilterVenueIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [filterStartDate, setFilterStartDate] = useState<DateValue | null>(
    null,
  );
  const [filterEndDate, setFilterEndDate] = useState<DateValue | null>(null);
  const [searchText, setSearchText] = useState("");
  const deferredSearch = useDeferredValue(searchText);

  const { data: divisions = [] } = useQuery({
    ...divisionsQueryOptions(),
    select: (divisions) =>
      divisions.map(({ id, display, name }) => ({
        value: id,
        display: display ?? name.toUpperCase(),
      })),
  });

  const trpc = useTRPC();
  const { data: venues = [] } = useQuery({
    ...trpc.venues.list.queryOptions(),
    select: (venues) =>
      venues.map(({ id, name, city }) => ({
        value: id,
        display: `${name}, ${city}`,
      })),
  });

  const searchParams = {
    divisionIds: Array.from(filterDivisionIds),
    venueIds: Array.from(filterVenueIds),
    startDate: filterStartDate?.toString(),
    endDate: filterEndDate?.toString(),
    excludeIds: existingSourceIds,
    search: deferredSearch || undefined,
    paging: { page: 1, size: BULK_PAGE_SIZE },
  };

  const hasFilters =
    filterDivisionIds.size > 0 ||
    filterVenueIds.size > 0 ||
    filterStartDate != null ||
    filterEndDate != null ||
    deferredSearch.length > 0;

  const { data, isLoading } = useQuery({
    ...trpc.tournaments.searchDivisions.queryOptions(searchParams),
    enabled: hasFilters,
  });

  const tournamentDivisions = data?.data ?? [];
  const totalCount = data?.pageInfo.totalItems ?? 0;

  const { mutate } = useMutation({
    ...bulkUpsertInviteSourcesMutationOptions(),
    onSuccess,
  });

  const form = useAppForm({
    defaultValues: {
      topN: 1,
    },
    onSubmit: ({ value }) => {
      if (tournamentDivisions.length === 0) return;

      mutate({
        sourceTournamentDivisionIds: tournamentDivisions.map((td) => td.id),
        targetTournamentId: tournamentId,
        topN: value.topN,
      });
    },
  });

  return (
    <div className="mt-4 flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-700">
        Add Source Divisions
      </h3>

      <TextField
        label="Search"
        placeholder="Tournament name..."
        value={searchText}
        onChange={setSearchText}
      />

      <Disclosure card={false}>
        <DisclosureHeader card={false} size="sm">
          More Filters
        </DisclosureHeader>
        <DisclosurePanel card={false}>
          <div className="grid grid-cols-6 items-end gap-2">
            <div className="col-span-3 flex flex-col gap-1">
              <span className="text-xs text-gray-500">Start date</span>
              <DateField
                aria-label="Start date"
                value={filterStartDate}
                onChange={setFilterStartDate}
                className="shrink-0"
              >
                <DateInput
                  className={(renderProps) =>
                    inputStyles({
                      ...renderProps,
                      class: "px-2 py-1 text-sm",
                    })
                  }
                >
                  {(segment) => (
                    <DateSegment segment={segment} className={segmentStyles} />
                  )}
                </DateInput>
              </DateField>
            </div>

            <div className="col-span-3 flex flex-col gap-1">
              <span className="text-xs text-gray-500">End date</span>
              <DateField
                aria-label="End date"
                value={filterEndDate}
                onChange={setFilterEndDate}
                className="shrink-0"
              >
                <DateInput
                  className={(renderProps) =>
                    inputStyles({
                      ...renderProps,
                      class: "px-2 py-1 text-sm",
                    })
                  }
                >
                  {(segment) => (
                    <DateSegment segment={segment} className={segmentStyles} />
                  )}
                </DateInput>
              </DateField>
            </div>

            <FilterDropdown
              label="Divisions"
              options={divisions}
              selectedValues={filterDivisionIds}
              onSelectionChange={setFilterDivisionIds}
              className="col-span-3"
            />

            <FilterDropdown
              label="Locations"
              options={venues}
              selectedValues={filterVenueIds}
              onSelectionChange={setFilterVenueIds}
              className="col-span-3"
            />
          </div>
        </DisclosurePanel>
      </Disclosure>

      <div className="flex flex-col gap-2">
        <p className="text-sm text-gray-600">
          {isLoading
            ? "Searching..."
            : `${totalCount} division${totalCount !== 1 ? "s" : ""} found`}
        </p>

        {!isLoading && tournamentDivisions.length === 0 && (
          <div className="rounded-lg border border-gray-300 p-4 text-center text-sm text-gray-500 italic">
            {hasFilters
              ? "No matching divisions found"
              : "Search for divisions to add as a source for invites to this tournament."}
          </div>
        )}

        {tournamentDivisions.length > 0 && (
          <ul className="max-h-48 space-y-1 overflow-auto rounded-lg border border-gray-300 p-2 text-sm">
            {tournamentDivisions.map((td) => {
              const division = td.division as Pick<
                Division,
                "maxAge" | "name" | "display"
              >;
              const tourney = td.tournament as {
                name: string;
                date: string;
                venue: { name: string; city: string };
              };
              const label = getTournamentDivisionDisplay({
                ...td,
                division,
              });
              return (
                <li key={td.id} className="text-gray-700">
                  {`${label} - ${tourney.venue.name}, ${tourney.venue.city} (${dateFormatter.format(parseDate(tourney.date).toDate(getDefaultTimeZone()))})`}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col items-end gap-3"
      >
        <form.AppField
          name="topN"
          children={(field) => (
            <field.Number
              field={field}
              label="Teams Receiving Bids"
              minValue={1}
              isRequired
              className="w-full"
            />
          )}
        />

        <form.AppForm>
          <form.SubmitButton
            requireChange={false}
            isDisabled={tournamentDivisions.length <= 0}
          >
            Add All ({totalCount})
          </form.SubmitButton>
        </form.AppForm>
      </form>
    </div>
  );
}
